import { FlaskConical, LoaderCircle, Play, RotateCcw, Trash2, TriangleAlert } from 'lucide-react';

import { Button } from '@/_shared/components/ui/button';
import { Card, CardContent } from '@/_shared/components/ui/card';

type ConfirmAction = 'cursor' | 'deliveries' | 'delete' | null;

export function ActionsBar({
  confirmAction,
  setConfirmAction,
  onRun,
  onDryRun,
  onResetCursor,
  onResetDeliveries,
  onDelete,
  runPending,
  dryRunPending,
  resetCursorPending,
  resetDeliveriesPending,
  deletePending,
  runError,
  dryRunError,
}: {
  confirmAction: ConfirmAction;
  setConfirmAction: (action: ConfirmAction) => void;
  onRun: () => void;
  onDryRun: () => void;
  onResetCursor: () => void;
  onResetDeliveries: () => void;
  onDelete: () => void;
  runPending: boolean;
  dryRunPending: boolean;
  resetCursorPending: boolean;
  resetDeliveriesPending: boolean;
  deletePending: boolean;
  runError: Error | null;
  dryRunError: Error | null;
}): React.ReactElement {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={onRun} disabled={runPending || dryRunPending}>
            {runPending ? (
              <LoaderCircle size={14} className="mr-1 animate-spin" />
            ) : (
              <Play size={14} className="mr-1" />
            )}
            Run Now
          </Button>
          <Button size="sm" variant="outline" onClick={onDryRun} disabled={runPending || dryRunPending}>
            {dryRunPending ? (
              <LoaderCircle size={14} className="mr-1 animate-spin" />
            ) : (
              <FlaskConical size={14} className="mr-1" />
            )}
            Dry Run
          </Button>

          {confirmAction === 'cursor' ? (
            <div className="flex items-center gap-1">
              <span className="text-xs text-amber-600">Reset cursor?</span>
              <Button size="sm" variant="destructive" onClick={onResetCursor} disabled={resetCursorPending}>
                Confirm
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmAction(null)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setConfirmAction('cursor')}>
              <RotateCcw size={14} className="mr-1" />
              Reset Cursor
            </Button>
          )}

          {confirmAction === 'deliveries' ? (
            <div className="flex items-center gap-1">
              <span className="text-xs text-amber-600">Clear all deliveries?</span>
              <Button size="sm" variant="destructive" onClick={onResetDeliveries} disabled={resetDeliveriesPending}>
                Confirm
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmAction(null)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setConfirmAction('deliveries')}>
              <Trash2 size={14} className="mr-1" />
              Clear Deliveries
            </Button>
          )}

          <div className="flex-1" />

          {confirmAction === 'delete' ? (
            <div className="flex items-center gap-1">
              <span className="text-xs text-red-600">Delete workflow?</span>
              <Button size="sm" variant="destructive" onClick={onDelete} disabled={deletePending}>
                {deletePending ? <LoaderCircle size={14} className="mr-1 animate-spin" /> : null}
                Delete
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmAction(null)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              className="text-red-500 hover:text-red-600"
              onClick={() => setConfirmAction('delete')}
            >
              <Trash2 size={14} className="mr-1" />
              Delete
            </Button>
          )}
        </div>

        {(runError ?? dryRunError) && (
          <div className="mt-3 flex items-center gap-2 text-xs text-red-500">
            <TriangleAlert size={14} />
            {(runError ?? dryRunError)?.message}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
